import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  useCreateHotspot,
  usePhoto360,
} from '@/features/interactive-viewer-v2/hooks/useHotspotData';
import type { ViewPosition } from '@/features/interactive-viewer-v2/types/types';
import { getErrorMessage } from '@/shared/errors/app-error';
import { Button } from '@/shared/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/form/form';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';

import {
  useCreateSafetyIssue,
  useSafetyPhotoContext,
  useSafetyTrades,
  useUpdateSafetyIssue,
} from './hooks';
import type { SafetyIssue, SafetyPinDraft } from './types';

const formSchema = z.object({
  description: z.string().trim().min(3, 'Describe the safety issue.'),
  trade: z.string().optional(),
  locationName: z
    .string()
    .trim()
    .min(1, 'Enter a location or area name.')
    .max(200, 'Location must be 200 characters or fewer.'),
});

type FormValues = z.infer<typeof formSchema>;

interface SafetyIssueFormProps {
  projectId: number;
  pin?: SafetyPinDraft | null;
  issue?: SafetyIssue;
  hotspotId?: number;
  photoId?: number;
  viewPosition?: ViewPosition | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export const SafetyIssueForm = ({
  projectId,
  pin,
  issue,
  hotspotId: existingHotspotId,
  photoId,
  viewPosition,
  onSuccess,
  onCancel,
}: SafetyIssueFormProps) => {
  const createIssue = useCreateSafetyIssue(projectId);
  const updateIssue = useUpdateSafetyIssue(projectId);
  const createHotspot = useCreateHotspot(String(projectId));
  const photo = usePhoto360(photoId, String(projectId));
  const photoContext = useSafetyPhotoContext(
    projectId,
    photoId,
    !issue && !pin && Boolean(photoId)
  );
  const trades = useSafetyTrades(projectId);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      description: issue?.description ?? '',
      trade: issue?.trade ?? 'none',
      locationName: issue?.location_detail?.name ?? pin?.locationName ?? '',
    },
  });

  const tradeOptions = useMemo(() => {
    const seen = new Set<string>();
    return (trades.data ?? []).filter((trade) => {
      const key = trade.name.trim().toLocaleLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [trades.data]);

  useEffect(() => {
    const location = photoContext.data?.location;
    if (!location || form.formState.dirtyFields.locationName) return;
    form.setValue('locationName', location.name, { shouldDirty: false });
  }, [form, form.formState.dirtyFields.locationName, photoContext.data?.location]);

  const onSubmit = async (values: FormValues) => {
    let hotspotId = existingHotspotId;
    if (!issue && !hotspotId && photoId && viewPosition) {
      const hotspot = await createHotspot.mutateAsync({ photoId, position: viewPosition });
      hotspotId = hotspot.id;
    }

    const locationName = values.locationName.trim();
    const sourceLocationId = issue?.location ?? pin?.locationId ?? photoContext.data?.location?.id;
    const sourceLocationName =
      issue?.location_detail?.name ?? pin?.locationName ?? photoContext.data?.location?.name ?? '';
    const usesExistingLocation = Boolean(
      sourceLocationId &&
        locationName.toLocaleLowerCase() === sourceLocationName.trim().toLocaleLowerCase()
    );
    const issueInput = {
      description: values.description,
      trade: values.trade && values.trade !== 'none' ? values.trade : null,
      location: usesExistingLocation ? sourceLocationId : null,
      location_name: !usesExistingLocation && locationName ? locationName : null,
      layout:
        pin?.layoutId ??
        photoContext.data?.layout ??
        photo.data?.layout_id ??
        issue?.layout ??
        null,
    };

    if (issue) {
      await updateIssue.mutateAsync({ issueId: issue.id, input: issueInput });
    } else {
      await createIssue.mutateAsync({
        project: projectId,
        ...issueInput,
        hotspot: hotspotId ?? null,
        position_x: pin?.x ?? photoContext.data?.position_x ?? null,
        position_y: pin?.y ?? photoContext.data?.position_y ?? null,
      });
    }
    form.reset();
    onSuccess();
  };

  const error = createIssue.error ?? updateIssue.error ?? createHotspot.error ?? photoContext.error;
  const submitting =
    createIssue.isPending ||
    updateIssue.isPending ||
    createHotspot.isPending ||
    photoContext.isLoading;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-1">
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  autoFocus
                  rows={4}
                  placeholder="Describe the hazard, unsafe condition, or action needed."
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="trade"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Trade</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select trade" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">No trade</SelectItem>
                    {tradeOptions.map((trade) => (
                      <SelectItem key={trade.id} value={trade.id}>
                        {trade.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="locationName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Location / area</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="e.g. Level 2 east corridor" />
                </FormControl>
                <FormDescription>
                  {pin?.locationId
                    ? `Matched the existing “${pin.locationName}” area from the pin position.`
                    : photoContext.isLoading
                      ? 'Matching the hotspot photo to its layout area…'
                      : photoContext.data?.location
                        ? `Matched the existing “${photoContext.data.location.name}” area from the hotspot photo.`
                        : 'No existing area matched. A name-only area tag will be created for this layout.'}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {getErrorMessage(error)}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting || photoContext.isError}>
            {submitting && <Loader2 className="mr-2 size-4 animate-spin" />}
            {issue ? 'Save changes' : 'Create safety issue'}
          </Button>
        </div>
      </form>
    </Form>
  );
};
